import { EventBus as _EventBus } from 'components/eventBus/EventBus';

import { createConfigConsumerScope } from './ConfigConsumer';
import { getFieldSpecificEventName, TConfigUpdatedKey } from './ConfigProvider.events';
import { TDeepPartial } from '../../types/deepPartial';
import { ObjectDeepDiff } from '../../utils/objectDeepDiff';

export const createConfig = <IConfig extends object>(defaultConfig: IConfig) => {
  let config = { ...defaultConfig };
  const _fallbackEventBus = new _EventBus();

  const { configConsumerConstructor, setConsumerConfig } =
    createConfigConsumerScope(_fallbackEventBus);

  setConsumerConfig(defaultConfig);

  const getConfigField: <K extends keyof IConfig>(configKey: K) => IConfig[K] = (configKey) =>
    config[configKey];

  const getConfig = () => config;

  const setConfig: (
    newConfig: TDeepPartial<IConfig>,
    eventBus: _EventBus<any>,
    sync?: boolean,
  ) => void = (newConfig, eventBus, sync = false) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const mergedConfig: IConfig = Object.entries(newConfig).reduce<IConfig>(
      (oldConfig, [newConfigKey, newConfigValues]) => {
        oldConfig[newConfigKey as keyof IConfig] = {
          ...oldConfig[newConfigKey as keyof IConfig],
          ...(newConfigValues as IConfig[keyof IConfig]),
        };

        return oldConfig;
      },
      { ...config },
    );

    const diff: Partial<IConfig> = ObjectDeepDiff(config, mergedConfig);
    config = mergedConfig;

    Object.entries(diff).forEach(([key, value]) => {
      const eventName: TConfigUpdatedKey = getFieldSpecificEventName(key as keyof IConfig);
      if (sync) {
        eventBus.publishSync(eventName, {
          updatedValues: value,
          newConfig: mergedConfig,
        });
      } else {
        eventBus.publish(eventName, {
          updatedValues: value,
          newConfig: mergedConfig,
        });
      }
    });

    setConsumerConfig(config);
  };

  const resetConfig = () => {
    config = { ...defaultConfig };
  };

  return {
    getConfigField,
    setConfig,
    getConfig,
    resetConfig,
    ConfigConsumer: configConsumerConstructor,
  };
};
